import type { ISongPlaybackWindow } from './slideshowAudioTimeline';

/**
 * Fixed automatic fade length for slideshow soundtrack clips (FOR-16403).
 * Not configurable in the editor — applied identically in Studio preview and client playback.
 */
export const SLIDESHOW_AUDIO_FADE_DURATION_MS = 2000;

/**
 * How close trim-end may sit to the file duration and still count as "natural" (untrimmed).
 * Song catalog durations are whole seconds; HTMLAudioElement.duration is a float, so a 1s
 * tolerance avoids falsely treating a full-length clip as trimmed.
 */
export const SLIDESHOW_AUDIO_END_TRIM_TOLERANCE_MS = 1000;

/**
 * Caps the fade so a short trim still has audible content in the middle
 * (fade-in and fade-out each take at most half the window).
 */
export function getEffectiveFadeDurationMs(windowDurationMs: number): number {
    if (windowDurationMs <= 0) {
        return 0;
    }

    return Math.min(SLIDESHOW_AUDIO_FADE_DURATION_MS, windowDurationMs / 2);
}

/**
 * True when playback stops more than {@link SLIDESHOW_AUDIO_END_TRIM_TOLERANCE_MS} before the
 * song file ends — i.e. the user (or a short placement) cut the natural ending off.
 */
export function isSongEndTrimmed(trimEndMs: number, songDurationMs: number): boolean {
    if (
        !Number.isFinite(songDurationMs) ||
        songDurationMs <= 0 ||
        !Number.isFinite(trimEndMs) ||
        trimEndMs < 0
    ) {
        return false;
    }

    return trimEndMs < songDurationMs - SLIDESHOW_AUDIO_END_TRIM_TOLERANCE_MS;
}

interface IFadedPlaybackVolumeParams {
    volume: number;
    currentTimeMs: number;
    window: ISongPlaybackWindow;
    /**
     * Full song file length in ms (typically `HTMLAudioElement.duration * 1000`). When unknown,
     * fade-out is skipped — better a hard cut than fading a natural ending.
     */
    songDurationMs?: number;
}

/**
 * Playhead-driven gain for a song window: fade-in when the natural song start was trimmed off
 * (`trimStartMs > 0`), and fade-out only when the natural song end was trimmed off
 * (`trimEndMs` meaningfully before `songDurationMs`). Scrubs stay in sync because volume is
 * derived from the timeline position each frame.
 */
export function getFadedPlaybackVolume({
    volume,
    currentTimeMs,
    window,
    songDurationMs = 0
}: IFadedPlaybackVolumeParams): number {
    const windowDurationMs = window.timelineEndMs - window.timelineStartMs;
    const fadeMs = getEffectiveFadeDurationMs(windowDurationMs);

    if (fadeMs <= 0 || volume <= 0) {
        return volume;
    }

    const msSinceStart = currentTimeMs - window.timelineStartMs;
    const msUntilEnd = window.timelineEndMs - currentTimeMs;

    let gain = 1;

    // Fade in only when the front of the song was trimmed — natural starts stay at full volume.
    if (window.trimStartMs > 0 && msSinceStart < fadeMs) {
        gain = Math.min(gain, Math.max(0, msSinceStart / fadeMs));
    }

    // Fade out only when the end was trimmed — natural endings keep their full tail.
    if (isSongEndTrimmed(window.trimEndMs, songDurationMs) && msUntilEnd < fadeMs) {
        gain = Math.min(gain, Math.max(0, msUntilEnd / fadeMs));
    }

    return volume * gain;
}
